import React from 'react'
import { userService } from '../../_services'
import { auth } from '../../firebase'
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth'
import { trackEvent } from '../../_services/fbpixel'
import { APP_NAME } from '../../brand'

const ROLES = [
  { value: 'buyer_agent', icon: '🏡', label: "I'm a buyer's agent", desc: 'I need showings covered when I can\'t be there' },
  { value: 'showing_agent', icon: '🔑', label: "I'm a showing agent", desc: 'I want to earn money showing homes near me' },
  { value: 'both', icon: '🤝', label: 'Both', desc: 'I\'ll post showings and cover them' }
]

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC']

class CompleteProfile extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      step: 1,
      user_type: 'buyer_agent',
      name: '',
      phone_number: '',
      license_number: '',
      license_state: '',
      brokerage: '',
      license_expires: '',
      coverage_city: '',
      coverage_radius: 25,
      otp: '',
      error: '',
      loading: false,
      confirmationResult: null,
      otpTimer: 0,
      ready: false
    }
    this.sendOtp = this.sendOtp.bind(this)
    this.verifyAndSubmit = this.verifyAndSubmit.bind(this)
    this.resendOtp = this.resendOtp.bind(this)
    this.submitProfile = this.submitProfile.bind(this)
  }

  componentDidMount() {
    userService.ready.then(() => {
      const user = userService.currentUserValue
      if (!user) {
        window.location.href = '/?login=true'
      } else if (user.status === 'active') {
        this.props.history.push('/')
      } else {
        this.setState({ ready: true, name: user.name || '' })
        trackEvent('CompleteRegistration', { content_name: 'signup' })
      }
    })
  }

  componentWillUnmount() {
    if (this.timerInterval) clearInterval(this.timerInterval)
    this.clearRecaptcha()
  }

  clearRecaptcha() {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear()
      window.recaptchaVerifier = null
    }
  }

  setupRecaptcha() {
    if (window.recaptchaVerifier) {
      try { window.recaptchaVerifier.clear() } catch (e) {}
      window.recaptchaVerifier = null
    }
    window.recaptchaVerifier = new RecaptchaVerifier('recaptcha-container', {
      size: 'invisible'
    }, auth)
  }

  startTimer() {
    this.setState({ otpTimer: 60 })
    this.timerInterval = setInterval(() => {
      this.setState(prev => {
        if (prev.otpTimer <= 1) {
          clearInterval(this.timerInterval)
          return { otpTimer: 0 }
        }
        return { otpTimer: prev.otpTimer - 1 }
      })
    }, 1000)
  }

  needsLicense() {
    return true // every user is a licensed agent on this platform
  }

  showsCoverage() {
    return this.state.user_type === 'showing_agent' || this.state.user_type === 'both'
  }

  validateDetails() {
    const { name, phone_number, license_number, license_state, coverage_city } = this.state
    if (!name.trim()) return 'Your name is required.'
    if (!/^\d{10}$/.test(phone_number)) return 'Enter a 10-digit US phone number.'
    if (!license_number.trim()) return 'Your license number is required.'
    if (!license_state) return 'Pick the state that issued your license.'
    if (this.showsCoverage() && !coverage_city.trim()) return 'Tell us the city you cover.'
    return null
  }

  profilePayload(firebaseIdToken) {
    const s = this.state
    const payload = {
      user_type: s.user_type,
      name: s.name.trim(),
      license: {
        number: s.license_number.trim(),
        state: s.license_state,
        brokerage: s.brokerage.trim(),
        expires: s.license_expires || undefined
      }
    }
    if (this.showsCoverage()) {
      payload.coverage = { city: s.coverage_city.trim(), radius_miles: s.coverage_radius }
    }
    payload.phone_number = '+1' + s.phone_number
    if (firebaseIdToken) payload.firebase_id_token = firebaseIdToken
    return payload
  }

  async sendOtp() {
    const err = this.validateDetails()
    if (err) return this.setState({ error: err })

    // Firebase not configured (local dev): skip OTP, submit directly —
    // the backend only accepts this when SKIP_PHONE_VERIFICATION=true
    if (!auth) {
      return this.submitProfile(null)
    }

    this.setState({ loading: true, error: '' })
    try {
      this.setupRecaptcha()
      const e164 = '+1' + this.state.phone_number
      const confirmationResult = await signInWithPhoneNumber(auth, e164, window.recaptchaVerifier)
      this.setState({ confirmationResult, loading: false, step: 3 })
      this.startTimer()
    } catch (e) {
      console.error('Firebase phone auth error:', e)
      let msg = 'Could not send the code — try again.'
      if (e.code === 'auth/invalid-phone-number') msg = 'That phone number doesn\'t look right.'
      else if (e.code === 'auth/too-many-requests') msg = 'Too many attempts — wait a bit and try again.'
      else if (e.code === 'auth/quota-exceeded') msg = 'SMS limit reached — try again later.'
      else if (e.code === 'auth/invalid-app-credential' || e.code === 'auth/captcha-check-failed') msg = 'Verification hiccup — reload the page and try again.'
      this.setState({ error: msg, loading: false })
    }
  }

  async resendOtp() {
    this.setState({ confirmationResult: null, otp: '' })
    await this.sendOtp()
  }

  async submitProfile(firebaseIdToken) {
    this.setState({ loading: true, error: '' })
    try {
      await userService.completeProfile(this.profilePayload(firebaseIdToken))
      trackEvent('Lead', { content_name: 'profile_completed', content_category: this.state.user_type })
      this.props.history.push(this.showsCoverage() ? '/jobs' : '/showings/post')
    } catch (e) {
      this.setState({ error: 'Could not save your profile — try again.', loading: false })
    }
  }

  async verifyAndSubmit() {
    const { otp, confirmationResult } = this.state
    if (otp.length !== 6) {
      return this.setState({ error: 'Enter the 6-digit code.' })
    }
    if (!confirmationResult) {
      return this.setState({ error: 'Something went wrong — resend the code.', step: 2 })
    }
    this.setState({ loading: true, error: '' })
    try {
      const credential = await confirmationResult.confirm(otp)
      const firebaseIdToken = await credential.user.getIdToken()
      await auth.signOut()
      await this.submitProfile(firebaseIdToken)
    } catch (e) {
      console.error('OTP verification error:', e)
      if (e.code === 'auth/invalid-verification-code') {
        this.setState({ error: 'That code isn\'t right.', loading: false })
      } else if (e.code === 'auth/code-expired') {
        this.setState({ error: 'Code expired — request a new one.', loading: false })
      } else {
        this.setState({ error: 'Verification failed.', loading: false })
      }
    }
  }

  render() {
    const { step, user_type, error, loading, otp, otpTimer, ready } = this.state
    if (!ready) return null

    return (
      <div className="ob-page">
        <div className="ob-card">
          <div className="ob-brand">
            <span className="ng-wordmark-key">⌂</span> {APP_NAME}
          </div>
          <h1 className="ob-title">
            {step === 1 ? 'How will you use ' + APP_NAME + '?' : step === 2 ? 'Set up your agent profile' : 'Verify your phone'}
          </h1>

          <div className="ob-progress">
            {[1, 2, 3].map(n => <span key={n} className={'ob-progress-dot' + (step >= n ? ' on' : '')} />)}
          </div>

          {step === 1 ? (
            <div>
              <div className="ob-roles">
                {ROLES.map(r => (
                  <button key={r.value} type="button"
                    className={'ob-role' + (user_type === r.value ? ' active' : '')}
                    onClick={() => this.setState({ user_type: r.value })}>
                    <span className="ob-role-icon">{r.icon}</span>
                    <span className="ob-role-label">{r.label}</span>
                    <span className="ob-role-desc">{r.desc}</span>
                  </button>
                ))}
              </div>
              <button className="sh-btn sh-btn-primary ob-next"
                onClick={() => this.setState({ step: 2, error: '' })}>
                Continue
              </button>
            </div>
          ) : null}

          {step === 2 ? (
            <div>
              <div className="sh-form-grid">
                <div className="sh-field full">
                  <label>Full name</label>
                  <input value={this.state.name} onChange={e => this.setState({ name: e.target.value })} placeholder="Jordan Smith" />
                </div>
                <div className="sh-field full">
                  <label>Mobile phone</label>
                  <div className="ob-phone">
                    <span>+1</span>
                    <input type="tel" maxLength="10" placeholder="5125550100"
                      value={this.state.phone_number}
                      onChange={e => this.setState({ phone_number: e.target.value.replace(/\D/g, '') })} />
                  </div>
                  <span className="hint">We'll text a verification code to this number.</span>
                </div>
                <div className="sh-field">
                  <label>License number</label>
                  <input value={this.state.license_number} onChange={e => this.setState({ license_number: e.target.value })} placeholder="0123456" />
                </div>
                <div className="sh-field">
                  <label>License state</label>
                  <select value={this.state.license_state} onChange={e => this.setState({ license_state: e.target.value })}>
                    <option value="">Select…</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="sh-field">
                  <label>Brokerage <span style={{ fontWeight: 400 }}>(optional)</span></label>
                  <input value={this.state.brokerage} onChange={e => this.setState({ brokerage: e.target.value })} placeholder="Keller Williams Austin" />
                </div>
                <div className="sh-field">
                  <label>License expires <span style={{ fontWeight: 400 }}>(optional)</span></label>
                  <input type="date" value={this.state.license_expires} onChange={e => this.setState({ license_expires: e.target.value })} />
                </div>
                {this.showsCoverage() ? (
                  <>
                    <div className="sh-field">
                      <label>City you cover</label>
                      <input value={this.state.coverage_city} onChange={e => this.setState({ coverage_city: e.target.value })} placeholder="Austin" />
                    </div>
                    <div className="sh-field">
                      <label>Travel radius</label>
                      <select value={this.state.coverage_radius} onChange={e => this.setState({ coverage_radius: e.target.value })}>
                        <option value="10">10 miles</option>
                        <option value="25">25 miles</option>
                        <option value="50">50 miles</option>
                      </select>
                    </div>
                  </>
                ) : null}
              </div>

              <p className="ob-note">
                Your license is verified by our team before you can accept showings — usually within one business day.
              </p>

              {error ? <div className="sh-error">{error}</div> : null}

              <div className="sh-actions">
                <button className="sh-btn sh-btn-outline" onClick={() => this.setState({ step: 1, error: '' })}>Back</button>
                <button className="sh-btn sh-btn-primary" disabled={loading} onClick={this.sendOtp}>
                  {loading ? 'Sending…' : (auth ? 'Text me the code' : 'Finish')}
                </button>
              </div>
            </div>
          ) : null}

          <div id="recaptcha-container"></div>

          {step === 3 ? (
            <div>
              <p className="ob-note" style={{ textAlign: 'center' }}>
                Enter the 6-digit code we texted to <b>+1 {this.state.phone_number}</b>
              </p>
              <div className="sh-field">
                <input className="ob-otp" type="text" maxLength="6" placeholder="——————"
                  value={otp}
                  onChange={e => this.setState({ otp: e.target.value.replace(/\D/g, '').slice(0, 6) })} />
              </div>
              {error ? <div className="sh-error">{error}</div> : null}
              <button className="sh-btn sh-btn-primary ob-next" disabled={loading} onClick={this.verifyAndSubmit}>
                {loading ? 'Verifying…' : 'Verify & finish'}
              </button>
              <p style={{ textAlign: 'center', marginTop: 12, fontSize: 13 }}>
                {otpTimer > 0 ? (
                  <span style={{ color: 'var(--ng-muted)' }}>Resend in {otpTimer}s</span>
                ) : (
                  <button className="ng-link-btn" onClick={this.resendOtp} disabled={loading}>
                    Didn't get it? <b>Resend code</b>
                  </button>
                )}
              </p>
              <button className="ng-link-btn" style={{ display: 'block', margin: '4px auto 0' }}
                onClick={() => {
                  this.clearRecaptcha()
                  this.setState({ step: 2, otp: '', error: '', confirmationResult: null })
                }}>
                ← Change phone number
              </button>
            </div>
          ) : null}
        </div>
      </div>
    )
  }
}

export default CompleteProfile
